// @file Surface custom property responses composable (ratings and votes)
import { captureFetchException } from '@@/bits/error_tracker'
import { __ } from '@@/bits/intl'
import { getCustomPropertyValue, isWallCustomPostPropertyOfType, setCustomPropertyValue } from '@@/bits/post_properties'
import { HttpCode, SnackbarNotificationType } from '@@/enums'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceGuestStore } from '@@/pinia/surface_guest_store'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import type {
  DeleteCustomPropertyResponsePayload,
  UpsertCustomPropertyResponsePayload,
} from '@@/surface/api/custom_property_response'
import { CustomPropertyResponseApi } from '@@/surface/api/custom_property_response'
import { VoteOrRatingFieldValue, WallCustomPostProperty } from '@@/types'
import type { FetchError } from '@padlet/fetch'
import { getNewVoteOrRatingFieldData } from '@padlet/universal-post-editor/src/bits/custom_property_responses'

interface UpdateAggregatedCustomPropertyResponseRealtimePayload {
  wish_id: number
  custom_property_id: string
  sum: number
  count: number
  user_hashid: string
}

interface UpdateUserCustomPropertyResponseRealtimePayload {
  wish_id: number
  custom_property_id: string
  int_value: number
}

interface DeleteUserCustomPropertyResponseRealtimePayload {
  id: number
  wish_id: number
  custom_property_id: string
}

const DELETE_CUSTOM_PROPERTY_RESPONSE_SNACKBAR_MESSAGES: Partial<
  Record<WallCustomPostProperty['data_type'] | 'default', string>
> = {
  vote: __('This vote cannot be removed because the post cannot be empty.'),
  rating: __('This rating cannot be removed because the post cannot be empty.'),
  default: __('This value cannot be removed because the post cannot be empty.'),
}

export const useSurfaceCustomPropertyResponse = (): {
  createOrUpdateCustomPropertyResponse: typeof createOrUpdateCustomPropertyResponse
  deleteCustomPropertyResponse: typeof deleteCustomPropertyResponse
  updateAggregatedCustomPropertyResponseRealtime: typeof updateAggregatedCustomPropertyResponseRealtime
  updateUserCustomPropertyResponseRealtime: typeof updateUserCustomPropertyResponseRealtime
  deleteUserCustomPropertyResponseRealtime: typeof deleteUserCustomPropertyResponseRealtime
} => {
  const globalSnackbarStore = useGlobalSnackbarStore()
  const surfaceGuestStore = useSurfaceGuestStore()
  const surfacePostsStore = useSurfacePostsStore()
  const surfacePostPropertiesStore = useSurfacePostPropertiesStore()

  const createOrUpdateCustomPropertyResponse = async (payload: UpsertCustomPropertyResponsePayload): Promise<void> => {
    // Get the post by hashid
    const post = Object.values(surfacePostsStore.postEntitiesById).find((p) => p?.hashid === payload.wishHashId)
    if (post == null) {
      void globalSnackbarStore.genericFetchError()
      return
    }

    // Check if guest needs to identify
    if (surfaceGuestStore.shouldShowGuestIdModal) {
      surfaceGuestStore.showGuestIdModal({
        afterSaveActions: [async () => await createOrUpdateCustomPropertyResponse(payload)],
      })
      return
    } else if (surfaceGuestStore.shouldUpdateNameBeforePublishing) {
      surfaceGuestStore.setUserName(surfaceGuestStore.displayName)
      await surfaceGuestStore.updateSessionUserName({
        shouldModerate: false,
      })
    }

    const { customPropertyId } = payload

    const wallCustomProperty = surfacePostPropertiesStore.wallCustomPostPropertiesById[customPropertyId]
    if (
      !isWallCustomPostPropertyOfType(wallCustomProperty, 'vote') &&
      !isWallCustomPostPropertyOfType(wallCustomProperty, 'rating')
    ) {
      return
    }

    const oldVoteFieldData = getCustomPropertyValue(post, wallCustomProperty)
    const newVoteOrRatingFieldData = getNewVoteOrRatingFieldData(oldVoteFieldData, payload.value)

    try {
      surfacePostsStore.updatePostInStore({
        ...post,
        custom_properties: {
          ...post.custom_properties,
          [customPropertyId]: newVoteOrRatingFieldData,
        },
      })

      await CustomPropertyResponseApi.upsert(payload)
    } catch (error) {
      // Revert the optimistic update
      if (post.id) {
        surfacePostsStore.updatePostInStore({
          ...post,
          custom_properties: {
            ...post.custom_properties,
            [customPropertyId]: oldVoteFieldData,
          },
        })
      }

      if (error?.status === HttpCode.Forbidden) {
        globalSnackbarStore.setSnackbar({
          message: __('You do not have permission to respond to this custom field.'),
          notificationType: SnackbarNotificationType.error,
        })
      } else {
        globalSnackbarStore.genericFetchError()
        captureFetchException(error as FetchError, {
          source: 'createOrUpdateCustomPropertyResponse',
        })
      }
    }
  }

  const deleteCustomPropertyResponse = async (payload: DeleteCustomPropertyResponsePayload): Promise<void> => {
    // Get the post by hashid
    const post = Object.values(surfacePostsStore.postEntitiesById).find((p) => p?.hashid === payload.wishHashId)
    if (post == null) {
      void globalSnackbarStore.genericFetchError()
      return
    }

    const { customPropertyId } = payload

    // Store original values for revert
    const oldVoteFieldData = post.custom_properties?.[customPropertyId] as VoteOrRatingFieldValue | undefined
    const newVoteOrRatingFieldData = getNewVoteOrRatingFieldData(oldVoteFieldData, undefined)

    try {
      surfacePostsStore.updatePostInStore({
        ...post,
        custom_properties: {
          ...post.custom_properties,
          [customPropertyId]: newVoteOrRatingFieldData,
        },
      })

      await CustomPropertyResponseApi.delete({ wishHashId: payload.wishHashId, customPropertyId })
    } catch (error) {
      // Revert the optimistic update
      if (post.id) {
        surfacePostsStore.updatePostInStore({
          ...post,
          custom_properties: {
            ...post.custom_properties,
            [customPropertyId]: oldVoteFieldData,
          },
        })
      }

      if (error?.status === HttpCode.Forbidden) {
        globalSnackbarStore.setSnackbar({
          message: __('You do not have permission to respond to this custom field.'),
          notificationType: SnackbarNotificationType.error,
        })
      } else if (error?.status === HttpCode.UnprocessableEntity && error?.message?.includes('Post cannot be empty')) {
        const wallCustomProperty = surfacePostPropertiesStore.wallCustomPostPropertiesById[payload.customPropertyId]
        const snackbarMessage =
          DELETE_CUSTOM_PROPERTY_RESPONSE_SNACKBAR_MESSAGES[wallCustomProperty?.data_type] ??
          (DELETE_CUSTOM_PROPERTY_RESPONSE_SNACKBAR_MESSAGES.default as string)

        globalSnackbarStore.setSnackbar({
          message: snackbarMessage,
          notificationType: SnackbarNotificationType.error,
        })
      } else {
        globalSnackbarStore.genericFetchError()
        captureFetchException(error as FetchError, {
          source: 'deleteCustomPropertyResponse',
        })
      }
    }
  }

  // #region handlers for realtime updates
  const updateAggregatedCustomPropertyResponseRealtime = async (
    payload: UpdateAggregatedCustomPropertyResponseRealtimePayload,
  ) => {
    const { wish_id: wishId, custom_property_id: customPropertyId, sum, count } = payload
    const post = surfacePostsStore.postEntitiesById[wishId]

    if (post == null) return

    const wallProperty = surfacePostPropertiesStore.wallCustomPostPropertiesById[customPropertyId]
    if (
      !isWallCustomPostPropertyOfType(wallProperty, 'rating') &&
      !isWallCustomPostPropertyOfType(wallProperty, 'vote')
    ) {
      return
    }

    const existingValue = getCustomPropertyValue(post, wallProperty)

    surfacePostsStore.updatePostInStore({
      id: wishId,
      ...setCustomPropertyValue(post, wallProperty, { ...existingValue, ...{ sum, count } }),
    })
  }

  const updateUserCustomPropertyResponseRealtime = async (payload: UpdateUserCustomPropertyResponseRealtimePayload) => {
    const { wish_id: wishId, custom_property_id: customPropertyId, int_value: intValue } = payload
    const post = surfacePostsStore.postEntitiesById[wishId]

    if (post == null) return

    const wallProperty = surfacePostPropertiesStore.wallCustomPostPropertiesById[customPropertyId]

    if (
      !isWallCustomPostPropertyOfType(wallProperty, 'rating') &&
      !isWallCustomPostPropertyOfType(wallProperty, 'vote')
    ) {
      return
    }

    const existingValue = getCustomPropertyValue(post, wallProperty)

    surfacePostsStore.updatePostInStore({
      id: wishId,
      ...setCustomPropertyValue(post, wallProperty, { ...existingValue, user_response: intValue }),
    })
  }

  const deleteUserCustomPropertyResponseRealtime = async (payload: DeleteUserCustomPropertyResponseRealtimePayload) => {
    const { wish_id: wishId, custom_property_id: customPropertyId } = payload
    const post = surfacePostsStore.postEntitiesById[wishId]

    if (post == null) return

    const wallProperty = surfacePostPropertiesStore.wallCustomPostPropertiesById[customPropertyId]
    if (
      !isWallCustomPostPropertyOfType(wallProperty, 'rating') &&
      !isWallCustomPostPropertyOfType(wallProperty, 'vote')
    ) {
      return
    }

    const existingValue = getCustomPropertyValue(post, wallProperty)
    if (existingValue == null) return
    const { user_response: _, ...valueWithoutUserResponse } = existingValue
    surfacePostsStore.updatePostInStore({
      id: wishId,
      ...setCustomPropertyValue(post, wallProperty, valueWithoutUserResponse),
    })
  }
  // #endregion

  return {
    createOrUpdateCustomPropertyResponse,
    deleteCustomPropertyResponse,
    // Realtime message handlers
    updateAggregatedCustomPropertyResponseRealtime,
    updateUserCustomPropertyResponseRealtime,
    deleteUserCustomPropertyResponseRealtime,
  }
}
